/**
 * Il deposito degli intenti, dalla parte della pagina.
 *
 * **`localStorage` non e' piu' l'autorita'.** Non perche' IndexedDB sia piu'
 * moderno, ma perche' cio' che il banco deposita non e' piu' una lista di
 * verdetti da spedire in ordine: e' un insieme di oggetti con uno stato,
 * interrogato per sessione e per stato a ogni ripresa. Su `localStorage` ogni
 * domanda e' «leggi tutto, riscrivi tutto», e la riscrittura di tutto da due
 * schede insieme perde il lavoro di una delle due — senza dirlo.
 *
 * **Cio' che c'era prima non si butta.** Un aggiornamento della pagina che
 * cancella in silenzio la coda di un operatore e' mezz'ora di lavoro persa
 * senza che nessuno lo sappia. Le righe vecchie si adottano: restano visibili
 * come lavoro **non** consegnato, e nessuna di esse verra' mai applicata,
 * perche' senza lease, sessione ed era nessuno puo' dire su quale stato del
 * candidato erano state prese.
 */
import type { IntentKind, ReviewIntent } from './reviewProtocol'
import { DATABASE, SNAPSHOT_STORE, STORE, openReviewDatabase } from './reviewDatabase'

/** Il deposito di prima: si legge una volta per adottarlo, poi si dimentica. */
export const LEGACY_STORE = 'gridfly.outbox'

/**
 * La versione dello schema.
 *
 * Un numero e non «basta che ci sia»: due schede possono avere due versioni
 * della pagina aperte insieme, e IndexedDB usa questo numero per bloccare la
 * piu' vecchia invece di lasciarle scrivere righe che l'altra non sa leggere.
 */
export { DATABASE, SNAPSHOT_STORE, STORE }

export interface ReviewStore {
  readonly name: string
  readonly storeName: string
  indexes(): string[]
  put(intent: ReviewIntent): Promise<void>
  get(key: string): Promise<ReviewIntent | null>
  /** Tutti, nell'ordine in cui l'operatore ha deciso. */
  all(): Promise<ReviewIntent[]>
  bySession(sessionId: string): Promise<ReviewIntent[]>
  inState(sessionId: string, state: ReviewIntent['state']): Promise<ReviewIntent[]>
  remove(key: string): Promise<void>
  close(): void
}

function awaited<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((done, fail) => {
    request.onsuccess = () => {
      done(request.result)
    }
    request.onerror = () => {
      fail(request.error ?? new Error('deposito non raggiungibile'))
    }
  })
}

/** L'ordine di decisione. A parita' d'istante decide la chiave: senza, due
 *  decisioni prese nello stesso millisecondo si scambierebbero di posto a ogni
 *  lettura, e un ritiro potrebbe partire prima del verdetto che annulla. */
function byMoment(one: ReviewIntent, other: ReviewIntent): number {
  if (one.createdAt !== other.createdAt) return one.createdAt < other.createdAt ? -1 : 1
  return one.idempotencyKey < other.idempotencyKey ? -1 : 1
}

export function openStore(factory: IDBFactory = indexedDB): Promise<ReviewStore> {
  return openReviewDatabase([STORE], factory).then((db) => wrap(db))
}

function wrap(db: IDBDatabase): ReviewStore {
  function reading(): IDBObjectStore {
    return db.transaction(STORE, 'readonly').objectStore(STORE)
  }

  function writing(): IDBObjectStore {
    return db.transaction(STORE, 'readwrite').objectStore(STORE)
  }

  return {
    name: db.name,
    storeName: STORE,

    indexes(): string[] {
      return Array.from(reading().indexNames)
    },

    async put(intent: ReviewIntent): Promise<void> {
      await awaited(writing().put(intent))
    },

    async get(key: string): Promise<ReviewIntent | null> {
      return (await awaited(reading().get(key))) ?? null
    },

    async all(): Promise<ReviewIntent[]> {
      const found = (await awaited(reading().getAll())) as ReviewIntent[]
      return found.sort(byMoment)
    },

    async bySession(sessionId: string): Promise<ReviewIntent[]> {
      const found = (await awaited(
        reading().index('sessionId').getAll(sessionId),
      )) as ReviewIntent[]
      return found.sort(byMoment)
    },

    async inState(sessionId: string, state: ReviewIntent['state']): Promise<ReviewIntent[]> {
      const found = (await awaited(
        reading().index('sessionId_state').getAll([sessionId, state]),
      )) as ReviewIntent[]
      return found.sort(byMoment)
    },

    async remove(key: string): Promise<void> {
      await awaited(writing().delete(key))
    },

    close(): void {
      db.close()
    },
  }
}

/**
 * Un deposito che vive quanto la pagina.
 *
 * Serve dove IndexedDB non c'e': finestra privata, quota negata, un navigatore
 * che non lo espone. Le decisioni non sopravvivono al ricaricamento, e questo
 * e' un peggioramento vero — ma l'alternativa sarebbe tornare a scrivere senza
 * sessione, cioe' aggirare proprio il controllo che impedisce a due persone di
 * decidere lo stesso candidato. Meglio una coda fragile che un doppione.
 */
export function memoryStore(): ReviewStore {
  const rows = new Map<string, ReviewIntent>()
  const sorted = (): ReviewIntent[] => Array.from(rows.values()).sort(byMoment)
  return {
    name: DATABASE,
    storeName: STORE,
    indexes: () => [],
    put: (intent: ReviewIntent) => {
      rows.set(intent.idempotencyKey, { ...intent })
      return Promise.resolve()
    },
    get: (key: string) => Promise.resolve(rows.get(key) ?? null),
    all: () => Promise.resolve(sorted()),
    bySession: (sessionId: string) =>
      Promise.resolve(sorted().filter((one) => one.sessionId === sessionId)),
    inState: (sessionId: string, state: ReviewIntent['state']) =>
      Promise.resolve(
        sorted().filter((one) => one.sessionId === sessionId && one.state === state),
      ),
    remove: (key: string) => {
      rows.delete(key)
      return Promise.resolve()
    },
    close: () => rows.clear(),
  }
}

const PENDING: ReviewIntent['state'][] = ['local', 'decision_pending']

/** Quanti intenti aspettano ancora nel deposito gia' esistente.
 *
 * Non apre una seconda coda: legge lo stesso IndexedDB del banco. Se il
 * deposito manca, zero e' «non misurato qui», e la shell non inventa lavoro.
 */
export async function countPendingIntents(factory?: IDBFactory): Promise<number> {
  try {
    const store = await openStore(factory ?? indexedDB)
    try {
      const many = await store.all()
      return many.filter((one) => PENDING.includes(one.state)).length
    } finally {
      store.close()
    }
  } catch {
    return 0
  }
}

/** Una riga del vecchio deposito, come `outbox.ts` la scriveva. */
interface LegacyPending {
  key?: unknown
  intent?: {
    kind?: unknown
    detectionId?: unknown
    level?: unknown
    reasonCode?: unknown
    box?: unknown
  }
}

function legacyPayload(intent: NonNullable<LegacyPending['intent']>): {
  kind: IntentKind
  payload: Record<string, unknown>
} {
  if (intent.kind === 'reject') {
    return {
      kind: 'verdict',
      payload: { decision: 'rejected', reason_code: String(intent.reasonCode ?? '') },
    }
  }
  if (intent.kind === 'undo') return { kind: 'reversal', payload: {} }
  const payload: Record<string, unknown> = {
    decision: 'confirmed',
    final_level: Number(intent.level ?? 0),
  }
  if (intent.box) payload.box = intent.box
  return { kind: 'verdict', payload }
}

/**
 * Adotta cio' che era rimasto nel vecchio deposito.
 *
 * Le righe adottate nascono **obsolete**, e non e' una scorciatoia: prive di
 * lease, sessione ed era, nessuno puo' dire su quale stato del candidato erano
 * state prese, e applicarle sarebbe scrivere un verdetto alla cieca. Restano
 * perche' l'operatore le aveva date per registrate, e la mancanza si vedrebbe
 * solo alla consegna, quando l'anomalia non c'e'.
 *
 * Una volta sola: le righe gia' adottate si saltano, e il vecchio deposito si
 * svuota. Ripescarle a ogni apertura rimetterebbe sotto gli occhi
 * dell'operatore avvisi che ha gia' letto.
 */
export async function adopt(
  store: ReviewStore,
  storage: Storage | null,
): Promise<ReviewIntent[]> {
  if (!storage) return []
  let rows: LegacyPending[]
  try {
    const raw = storage.getItem(LEGACY_STORE)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    rows = parsed as LegacyPending[]
  } catch {
    // Un deposito illeggibile non deve impedire di lavorare: peggio sarebbe
    // una pagina che non si apre per colpa di cio' che c'e' dentro, perche'
    // l'operatore non ha modo di svuotarlo.
    return []
  }

  const adopted: ReviewIntent[] = []
  const when = new Date().toISOString()
  for (const row of rows) {
    const key = typeof row.key === 'string' ? row.key : null
    if (key === null || !row.intent || typeof row.intent.detectionId !== 'string') continue
    if ((await store.get(key)) !== null) continue
    const { kind, payload } = legacyPayload(row.intent)
    const intent: ReviewIntent = {
      idempotencyKey: key,
      sessionId: '',
      detectionId: row.intent.detectionId,
      leaseToken: null,
      reviewEpoch: 0,
      expectedCandidateVersion: 0,
      kind,
      payload,
      state: 'stale',
      createdAt: when,
      undoDeadline: null,
      origin: 'legacy',
      note: 'Presa prima dell\u2019ultimo aggiornamento: va ripresa e decisa di nuovo.',
    }
    await store.put(intent)
    adopted.push(intent)
  }
  storage.removeItem(LEGACY_STORE)
  return adopted
}
